"use client";

import { Button } from "@/components/ui/button";
import { useTour } from "@/context/TourContext";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import { LuChevronLeft, LuChevronRight, LuX } from "react-icons/lu";
import { SingleStepTooltip } from "./SingleStepTooltip";
import { TourTransitionTooltip } from "./TourTransitionTooltip";
import { getTourById, getTourInfo } from "./tourDefinitions";

interface TourOverlayProps {
  className?: string;
}

export const TourOverlay: React.FC<TourOverlayProps> = () => {
  const { tourState, nextStep, prevStep, completeTour, startTour } = useTour();

  // All hooks must be called before any conditional returns
  const [overlayState, setOverlayState] = useState({
    targetElement: null as HTMLElement | null,
    secondaryTargetElement: null as HTMLElement | null,
    spotlightRect: null as DOMRect | null,
    secondarySpotlightRect: null as DOMRect | null,
    isTransitioning: false,
    cachedTooltipHeight: null as number | null,
    cachedTooltipWidth: null as number | null,
  });

  // Estado para controlar la expansión de detalles
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  // Resetear estado de expansión cuando cambie el paso del tour
  useEffect(() => {
    setIsDetailsExpanded(false);
  }, [tourState.currentStep]);

  // Limpiar dimensiones cacheadas cuando cambie el estado de expansión
  useEffect(() => {
    setOverlayState((prev) => ({
      ...prev,
      cachedTooltipHeight: null,
      cachedTooltipWidth: null,
    }));
  }, [isDetailsExpanded]);

  // Check if this is a single-step tour
  const isSingleStepTour = tourState.steps.length === 1;

  const currentStep = tourState.steps[tourState.currentStep];

  // Find and highlight the target element
  useEffect(() => {
    if (!tourState.isActive || !currentStep) {
      return;
    }

    console.log(
      "Tour: Current step target =",
      currentStep.target,
      "action =",
      currentStep.action,
    );

    // Set transitioning state when step changes
    setOverlayState((prev) => ({
      ...prev,
      isTransitioning: true,
      targetElement: null,
      secondaryTargetElement: null,
      spotlightRect: null,
      secondarySpotlightRect: null,
    }));

    // Open sidebar automatically when reaching the filter sidebar step
    if (currentStep.target === "tour-employees-filter-sidebar") {
      if (
        (window as unknown as { toggleEmployeeFilterSidebar?: () => void })
          .toggleEmployeeFilterSidebar
      ) {
        (
          window as unknown as {
            toggleEmployeeFilterSidebar: () => void;
          }
        ).toggleEmployeeFilterSidebar();
      }
    }

    // Open tags menu automatically when reaching the tags menu step
    if (currentStep.target === "tour-employees-tags-menu") {
      if (
        (window as unknown as { openEmployeesTagsMenu?: () => void })
          .openEmployeesTagsMenu
      ) {
        (
          window as unknown as {
            openEmployeesTagsMenu: () => void;
          }
        ).openEmployeesTagsMenu();
      }
    }

    // Add extra delay for filter sidebar to prevent tooltip jumping
    const delay =
      currentStep.target === "tour-employees-filter-sidebar" ? 500 : 100;

    // Function to find and set target elements with retry mechanism
    const findAndSetTargetElements = (retryCount = 0, maxRetries = 10) => {
      const elements = document.querySelectorAll(
        `[data-tour="${currentStep.target}"]`,
      ) as NodeListOf<HTMLElement>;

      if (elements.length > 0) {
        // Verify the first element matches the current step
        const elementTourId = elements[0].getAttribute("data-tour");
        if (elementTourId === currentStep.target) {
          setOverlayState((prev) => ({ ...prev, targetElement: elements[0] }));

          // Calculate a bounding rect that encompasses all elements
          let minLeft = Infinity;
          let minTop = Infinity;
          let maxRight = -Infinity;
          let maxBottom = -Infinity;

          // For employee columns, filter for table cells and headers
          const elementsToHighlight =
            currentStep.target === "tour-scheduling-overview-employees"
              ? Array.from(elements).filter((el) => {
                  const isEmployeeElement =
                    el.getAttribute("data-tour") ===
                    "tour-scheduling-overview-employees";
                  const isTableCell = el.tagName === "TD";
                  const isTableHeader = el.tagName === "TH";
                  return isEmployeeElement && (isTableCell || isTableHeader);
                })
              : elements;

          elementsToHighlight.forEach((element) => {
            const rect = element.getBoundingClientRect();

            minLeft = Math.min(minLeft, rect.left);
            minTop = Math.min(minTop, rect.top);
            maxRight = Math.max(maxRight, rect.right);
            maxBottom = Math.max(maxBottom, rect.bottom);
          });

          const combinedRect = {
            left: minLeft,
            top: minTop,
            right: maxRight,
            bottom: maxBottom,
            width: maxRight - minLeft,
            height: maxBottom - minTop,
            x: minLeft,
            y: minTop,
            toJSON: () => ({}),
          } as DOMRect;

          setOverlayState((prev) => ({
            ...prev,
            spotlightRect: combinedRect,
            isTransitioning: false,
          }));

          if (
            currentStep.target === "tour-employees-filter-sidebar" &&
            combinedRect.height === 0
          ) {
            // Wait for sidebar to fully render
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds max

            const checkHeight = () => {
              attempts++;
              const newElements = document.querySelectorAll(
                `[data-tour="${currentStep.target}"]`,
              ) as NodeListOf<HTMLElement>;

              if (newElements.length > 0) {
                let minLeft = Infinity;
                let minTop = Infinity;
                let maxRight = -Infinity;
                let maxBottom = -Infinity;

                Array.from(newElements).forEach((element) => {
                  const rect = element.getBoundingClientRect();
                  minLeft = Math.min(minLeft, rect.left);
                  minTop = Math.min(minTop, rect.top);
                  maxRight = Math.max(maxRight, rect.right);
                  maxBottom = Math.max(maxBottom, rect.bottom);
                });

                const newCombinedRect = {
                  left: minLeft,
                  top: minTop,
                  right: maxRight,
                  bottom: maxBottom,
                  width: maxRight - minLeft,
                  height: maxBottom - minTop,
                  x: minLeft,
                  y: minTop,
                  toJSON: () => ({}),
                } as DOMRect;

                if (newCombinedRect.height > 0) {
                  setOverlayState((prev) => ({
                    ...prev,
                    spotlightRect: newCombinedRect,
                    isTransitioning: false,
                  }));
                } else if (attempts < maxAttempts) {
                  setTimeout(checkHeight, 100);
                } else {
                  setOverlayState((prev) => ({
                    ...prev,
                    spotlightRect: combinedRect,
                    isTransitioning: false,
                  }));
                }
              } else if (attempts < maxAttempts) {
                setTimeout(checkHeight, 100);
              } else {
                setOverlayState((prev) => ({
                  ...prev,
                  spotlightRect: combinedRect,
                  isTransitioning: false,
                }));
              }
            };
            setTimeout(checkHeight, 100);
            return;
          }

          elements[0].scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });

          // Handle automatic actions
          if (currentStep.action === "click") {
            console.log(
              "Tour: Executing click action for target =",
              currentStep.target,
            );
            // Add a small delay before clicking to ensure element is visible
            setTimeout(() => {
              // Check for specific targets first (most specific to least specific)
              if (currentStep.target === "tour-contracts-create") {
                // For contracts create button, just click it
                console.log("Tour: Clicking contracts create button");
                elements[0].click();
              } else if (
                (
                  window as unknown as {
                    changeContractsTab?: (tab: string) => void;
                  }
                ).changeContractsTab &&
                currentStep.target.includes("contracts") &&
                currentStep.target.includes("-tab")
              ) {
                // Extract tab value from target (e.g., "tour-contracts-schedule-tab" -> "schedule")
                const tabValue = currentStep.target
                  .replace("tour-contracts-", "")
                  .replace("-tab", "");
                (
                  window as unknown as {
                    changeContractsTab: (tab: string) => void;
                  }
                ).changeContractsTab(tabValue);
              } else if (
                (
                  window as unknown as {
                    changeEmployeesTab?: (tab: string) => void;
                  }
                ).changeEmployeesTab &&
                currentStep.target.includes("tour-employees-tab-") &&
                !currentStep.target.includes("-content")
              ) {
                // Extract tab value from target (e.g., "tour-employees-tab-education" -> "education")
                const tabValue = currentStep.target.replace(
                  "tour-employees-tab-",
                  "",
                );
                (
                  window as unknown as {
                    changeEmployeesTab: (tab: string) => void;
                  }
                ).changeEmployeesTab(tabValue);
              } else if (
                (
                  window as unknown as {
                    toggleEmployeeFilterSidebar?: () => void;
                  }
                ).toggleEmployeeFilterSidebar &&
                currentStep.target === "tour-employees-filter-button"
              ) {
                // Don't open sidebar automatically - let user click manually
              } else {
                // Fallback to click if global function is not available
                elements[0].click();

                const clickEvent = new MouseEvent("click", {
                  bubbles: true,
                  cancelable: true,
                  view: window,
                });
                elements[0].dispatchEvent(clickEvent);
              }

              // Handle secondary target for contracts create
              if (
                currentStep.target === "tour-contracts-create" &&
                currentStep.secondaryTarget
              ) {
                // Use global function to open dropdown immediately
                if (
                  (
                    window as unknown as {
                      openContractsCreateDropdown?: () => void;
                    }
                  ).openContractsCreateDropdown
                ) {
                  console.log(
                    "Tour: Opening contracts dropdown via global function",
                  );
                  (
                    window as unknown as {
                      openContractsCreateDropdown: () => void;
                    }
                  ).openContractsCreateDropdown();
                } else {
                  // Fallback to click events
                  const dropdownTrigger = elements[0] as HTMLElement;
                  dropdownTrigger.dispatchEvent(
                    new MouseEvent("click", { bubbles: true }),
                  );
                }

                // Wait for dropdown to be fully rendered and stable
                const waitForStableMenu = (attempt = 0, maxAttempts = 6) => {
                  const secondaryElement = document.querySelector(
                    `[data-tour="${currentStep.secondaryTarget}"]`,
                  ) as HTMLElement;

                  if (secondaryElement) {
                    const secondaryRect =
                      secondaryElement.getBoundingClientRect();

                    if (secondaryRect.width > 50 && secondaryRect.height > 50) {
                      console.log("Tour: Contracts menu found and stable");
                      setOverlayState((prev) => ({
                        ...prev,
                        secondaryTargetElement: secondaryElement,
                        secondarySpotlightRect: secondaryRect,
                      }));
                    } else {
                      console.log(
                        "Tour: Menu not fully visible yet, retrying...",
                      );
                      if (attempt < maxAttempts - 1) {
                        setTimeout(
                          () => waitForStableMenu(attempt + 1, maxAttempts),
                          50,
                        );
                      }
                    }
                  } else {
                    console.log("Tour: Menu element not found, retrying...");
                    if (attempt < maxAttempts - 1) {
                      setTimeout(
                        () => waitForStableMenu(attempt + 1, maxAttempts),
                        50,
                      );
                    }
                  }
                };

                setTimeout(() => {
                  waitForStableMenu();
                }, 100); // Much shorter initial delay
              } else if (
                currentStep.target === "tour-employees-add-candidate" &&
                currentStep.secondaryTarget
              ) {
                // Use global function to open dropdown immediately
                if (
                  (
                    window as unknown as {
                      openAddCandidateDropdown?: () => void;
                    }
                  ).openAddCandidateDropdown
                ) {
                  console.log(
                    "Tour: Opening add candidate dropdown via global function",
                  );
                  (
                    window as unknown as {
                      openAddCandidateDropdown: () => void;
                    }
                  ).openAddCandidateDropdown();
                } else {
                  // Fallback to click events
                  const dropdownTrigger = elements[0] as HTMLElement;
                  dropdownTrigger.dispatchEvent(
                    new MouseEvent("click", { bubbles: true }),
                  );
                }

                // Wait for dropdown to be fully rendered and stable
                const waitForStableMenu = (attempt = 0, maxAttempts = 6) => {
                  const secondaryElement = document.querySelector(
                    `[data-tour="${currentStep.secondaryTarget}"]`,
                  ) as HTMLElement;

                  if (secondaryElement) {
                    const secondaryRect =
                      secondaryElement.getBoundingClientRect();

                    if (secondaryRect.width > 50 && secondaryRect.height > 50) {
                      console.log("Tour: Add candidate menu found and stable");
                      setOverlayState((prev) => ({
                        ...prev,
                        secondaryTargetElement: secondaryElement,
                        secondarySpotlightRect: secondaryRect,
                      }));
                    } else {
                      console.log(
                        "Tour: Menu not fully visible yet, retrying...",
                      );
                      if (attempt < maxAttempts - 1) {
                        setTimeout(
                          () => waitForStableMenu(attempt + 1, maxAttempts),
                          50,
                        );
                      }
                    }
                  } else {
                    console.log("Tour: Menu element not found, retrying...");
                    if (attempt < maxAttempts - 1) {
                      setTimeout(
                        () => waitForStableMenu(attempt + 1, maxAttempts),
                        50,
                      );
                    }
                  }
                };

                setTimeout(() => {
                  waitForStableMenu();
                }, 100); // Much shorter initial delay
              } else if (
                currentStep.target === "tour-employees-tags" &&
                currentStep.secondaryTarget
              ) {
                // Use global function to open dropdown immediately
                if (
                  (
                    window as unknown as {
                      openEmployeesTagsMenu?: () => void;
                    }
                  ).openEmployeesTagsMenu
                ) {
                  console.log(
                    "Tour: Opening employees tags menu via global function",
                  );
                  (
                    window as unknown as {
                      openEmployeesTagsMenu: () => void;
                    }
                  ).openEmployeesTagsMenu();
                } else {
                  // Fallback to click events
                  const dropdownTrigger = elements[0] as HTMLElement;
                  dropdownTrigger.dispatchEvent(
                    new MouseEvent("click", { bubbles: true }),
                  );
                }

                // Wait for dropdown to be fully rendered and stable
                const waitForStableMenu = (attempt = 0, maxAttempts = 6) => {
                  const secondaryElement = document.querySelector(
                    `[data-tour="${currentStep.secondaryTarget}"]`,
                  ) as HTMLElement;

                  if (secondaryElement) {
                    const secondaryRect =
                      secondaryElement.getBoundingClientRect();

                    if (secondaryRect.width > 50 && secondaryRect.height > 50) {
                      console.log("Tour: Tags menu found and stable");
                      setOverlayState((prev) => ({
                        ...prev,
                        secondaryTargetElement: secondaryElement,
                        secondarySpotlightRect: secondaryRect,
                      }));
                    } else {
                      console.log(
                        "Tour: Menu not fully visible yet, retrying...",
                      );
                      if (attempt < maxAttempts - 1) {
                        setTimeout(
                          () => waitForStableMenu(attempt + 1, maxAttempts),
                          50,
                        );
                      }
                    }
                  } else {
                    console.log("Tour: Menu element not found, retrying...");
                    if (attempt < maxAttempts - 1) {
                      setTimeout(
                        () => waitForStableMenu(attempt + 1, maxAttempts),
                        50,
                      );
                    }
                  }
                };

                setTimeout(() => {
                  waitForStableMenu();
                }, 100); // Much shorter initial delay
              } else if (currentStep.secondaryTarget) {
                // For other secondary targets, use original logic
                const delay = currentStep.target.includes("tour-employees-tab-")
                  ? 800
                  : 200;

                setTimeout(() => {
                  const secondaryElement = document.querySelector(
                    `[data-tour="${currentStep.secondaryTarget}"]`,
                  ) as HTMLElement;

                  if (secondaryElement) {
                    const secondaryRect =
                      secondaryElement.getBoundingClientRect();
                    if (secondaryRect.width > 0 && secondaryRect.height > 0) {
                      setOverlayState((prev) => ({
                        ...prev,
                        secondaryTargetElement: secondaryElement,
                        secondarySpotlightRect: secondaryRect,
                      }));
                    }
                  }
                }, delay);
              }
            }, 300);
          }
        } else {
          setOverlayState((prev) => ({
            ...prev,
            targetElement: null,
            spotlightRect: null,
            isTransitioning: false,
          }));
          // element mismatch, ignore
        }
      } else {
        // Element not found - implement retry mechanism
        if (retryCount < maxRetries) {
          // Retry with exponential backoff
          const retryDelay = Math.min(100 * Math.pow(1.5, retryCount), 2000);
          setTimeout(() => {
            findAndSetTargetElements(retryCount + 1, maxRetries);
          }, retryDelay);
        } else {
          // Max retries reached, give up
          console.warn(
            `Tour target element "${currentStep.target}" not found after ${maxRetries} attempts`,
          );
          setOverlayState((prev) => ({
            ...prev,
            targetElement: null,
            spotlightRect: null,
            isTransitioning: false,
          }));
        }
      }

      // Handle secondary target if it exists
      if (currentStep.secondaryTarget) {
        // If there's a click action, don't search for secondary target immediately
        // It will be searched after the click action completes
        if (currentStep.action === "click") {
          setOverlayState((prev) => ({
            ...prev,
            secondaryTargetElement: null,
            secondarySpotlightRect: null,
          }));
        } else if (currentStep.target === "tour-employees-filter-button") {
          // For filter button step, don't search for secondary target immediately
          // The sidebar step will handle the sidebar highlight
          setOverlayState((prev) => ({
            ...prev,
            secondaryTargetElement: null,
            secondarySpotlightRect: null,
          }));
        } else {
          // Immediate search for secondary element
          const secondaryElement = document.querySelector(
            `[data-tour="${currentStep.secondaryTarget}"]`,
          ) as HTMLElement;

          if (secondaryElement) {
            const secondaryElementTourId =
              secondaryElement.getAttribute("data-tour");
            if (secondaryElementTourId === currentStep.secondaryTarget) {
              const secondaryRect = secondaryElement.getBoundingClientRect();
              setOverlayState((prev) => ({
                ...prev,
                secondaryTargetElement: secondaryElement,
                secondarySpotlightRect: secondaryRect,
              }));
            } else {
              setOverlayState((prev) => ({
                ...prev,
                secondaryTargetElement: null,
                secondarySpotlightRect: null,
              }));
            }
          } else {
            setOverlayState((prev) => ({
              ...prev,
              secondaryTargetElement: null,
              secondarySpotlightRect: null,
            }));
          }
        }
      } else {
        setOverlayState((prev) => ({
          ...prev,
          secondaryTargetElement: null,
          secondarySpotlightRect: null,
        }));
      }
    };

    // Call the function with initial delay
    const timer = setTimeout(() => {
      findAndSetTargetElements(0, 10);
    }, delay);

    return () => clearTimeout(timer);
  }, [tourState.isActive, tourState.currentStep, currentStep, nextStep]);

  // Update spotlight position on scroll/resize
  useEffect(() => {
    // if (!overlayState.targetElement || !tourState.isActive) return;

    if (!tourState.isActive || !currentStep) return;

    const updatePosition = () => {
      // Siempre buscamos fresco en el DOM. Así, si React destruye y vuelve a crear
      // la barra de filtros, la encontraremos inmediatamente.
      const elements = document.querySelectorAll(
        `[data-tour="${currentStep.target}"]`,
      ) as NodeListOf<HTMLElement>;

      if (elements.length > 0) {
        let minLeft = Infinity;
        let minTop = Infinity;
        let maxRight = -Infinity;
        let maxBottom = -Infinity;
        let hasValidRects = false;

        // For employee columns, only highlight the first few visible columns
        const elementsToHighlight =
          currentStep.target === "tour-scheduling-overview-employees"
            ? Array.from(elements).slice(0, 3)
            : elements;

        elementsToHighlight.forEach((element) => {
          const rect = element.getBoundingClientRect();
          // Ignorar elementos que React mantiene en el DOM pero oculta (width 0)
          if (rect.width > 0 && rect.height > 0) {
            minLeft = Math.min(minLeft, rect.left);
            minTop = Math.min(minTop, rect.top);
            maxRight = Math.max(maxRight, rect.right);
            maxBottom = Math.max(maxBottom, rect.bottom);
            hasValidRects = true;
          }
        });

        if (hasValidRects) {
          const combinedRect = {
            left: minLeft,
            top: minTop,
            right: maxRight,
            bottom: maxBottom,
            width: maxRight - minLeft,
            height: maxBottom - minTop,
            x: minLeft,
            y: minTop,
            toJSON: () => ({}),
          } as DOMRect;

          setOverlayState((prev) => {
            // Solo actualizamos si las coordenadas o el elemento cambiaron,
            // esto evita ciclos infinitos de re-renderizado.
            if (
              prev.targetElement !== elements[0] ||
              prev.spotlightRect?.top !== combinedRect.top ||
              prev.spotlightRect?.left !== combinedRect.left ||
              prev.spotlightRect?.width !== combinedRect.width ||
              prev.spotlightRect?.height !== combinedRect.height
            ) {
              return {
                ...prev,
                targetElement: elements[0],
                spotlightRect: combinedRect,
              };
            }
            return prev;
          });
        } else {
          // El elemento está en el DOM pero es invisible. Ocultamos el highlight.
          setOverlayState((prev) =>
            prev.spotlightRect !== null
              ? { ...prev, spotlightRect: null }
              : prev,
          );
        }
      } else {
        // ¡El elemento desapareció por completo! (Ej: Suspense se activó).
        // Ocultamos el highlight y borramos la referencia muerta.
        setOverlayState((prev) =>
          prev.spotlightRect !== null
            ? { ...prev, spotlightRect: null, targetElement: null }
            : prev,
        );
      }

      // Aplicamos la misma robustez para el objetivo secundario
      if (currentStep.secondaryTarget) {
        const secondaryElements = document.querySelectorAll(
          `[data-tour="${currentStep.secondaryTarget}"]`,
        ) as NodeListOf<HTMLElement>;

        if (secondaryElements.length > 0) {
          const rect = secondaryElements[0].getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            setOverlayState((prev) => {
              if (
                prev.secondaryTargetElement !== secondaryElements[0] ||
                prev.secondarySpotlightRect?.top !== rect.top ||
                prev.secondarySpotlightRect?.left !== rect.left
              ) {
                return {
                  ...prev,
                  secondaryTargetElement: secondaryElements[0],
                  secondarySpotlightRect: rect,
                };
              }
              return prev;
            });
          } else {
            setOverlayState((prev) =>
              prev.secondarySpotlightRect !== null
                ? { ...prev, secondarySpotlightRect: null }
                : prev,
            );
          }
        } else {
          setOverlayState((prev) =>
            prev.secondarySpotlightRect !== null
              ? {
                  ...prev,
                  secondarySpotlightRect: null,
                  secondaryTargetElement: null,
                }
              : prev,
          );
        }
      }
    };

    window.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("resize", updatePosition, { passive: true });

    // LA SOLUCIÓN: Agregamos un intervalo que escanea constantemente el DOM (cada 100ms).
    // Si la barra de filtros colapsa y vuelve a aparecer, este intervalo la captura
    // y restaura el tour automáticamente en un parpadeo.
    const intervalId = setInterval(updatePosition, 100);

    return () => {
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
      clearInterval(intervalId); // Limpiamos el intervalo
    };
  }, [tourState.isActive, currentStep]);

  // Monitor tooltip height changes and clear cache when step changes
  useEffect(() => {
    if (!tourState.isActive || !currentStep) return;

    // Clear cached height when step changes
    setOverlayState((prev) => ({ ...prev, cachedTooltipHeight: null }));

    // Force height recalculation after a short delay
    const timer = setTimeout(() => {
      const tooltipElement = document.querySelector(
        "[data-tour-tooltip]",
      ) as HTMLElement;
      if (tooltipElement) {
        const rect = tooltipElement.getBoundingClientRect();
        if (rect.height > 0) {
          setOverlayState((prev) => ({
            ...prev,
            cachedTooltipHeight: rect.height,
          }));
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [tourState.isActive, tourState.currentStep, currentStep, nextStep]);

  // Robust watcher for delayed-rendering secondary targets
  useEffect(() => {
    if (!tourState.isActive || !currentStep || !currentStep.secondaryTarget) {
      return;
    }

    // If we already have the secondary element/rect, no need to watch
    if (
      overlayState.secondaryTargetElement &&
      overlayState.secondarySpotlightRect
    ) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 30; // ~4.5s at 150ms
    const intervalMs = 150;
    const findSecondary = () => {
      const el = document.querySelector(
        `[data-tour="${currentStep.secondaryTarget}"]`,
      ) as HTMLElement | null;
      if (el) {
        const rect = el.getBoundingClientRect();
        setOverlayState((prev) => ({
          ...prev,
          secondaryTargetElement: el,
          secondarySpotlightRect: rect,
        }));
        return true;
      }
      return false;
    };

    // Try immediately once
    if (findSecondary()) {
      return;
    }

    const intervalId = window.setInterval(() => {
      attempts++;
      if (findSecondary() || attempts >= maxAttempts) {
        window.clearInterval(intervalId);
      }
    }, intervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    tourState.isActive,
    tourState.currentStep,
    currentStep,
    overlayState.secondaryTargetElement,
    overlayState.secondarySpotlightRect,
  ]);

  // Handle keyboard events for regular tour steps
  useEffect(() => {
    if (!tourState.isActive || !currentStep || currentStep.isTransition) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        event.preventDefault();
        nextStep();
      } else if (event.key === "Escape") {
        event.preventDefault();
        completeTour();
      } else if (event.key === "ArrowLeft" && tourState.currentStep > 0) {
        event.preventDefault();
        prevStep();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        nextStep();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    tourState.isActive,
    tourState.currentStep,
    currentStep,
    nextStep,
    prevStep,
    completeTour,
  ]);

  // If it's a single step tour, use the custom component
  if (isSingleStepTour) {
    return <SingleStepTooltip />;
  }

  if (!tourState.isActive || !currentStep) return null;

  // Handle transition steps
  if (currentStep.isTransition) {
    // Get tour names from definitions
    const getTourName = (tourId: string) => {
      const tourNames: Record<string, string> = {
        overview: "Tour General",
        contracts: "Tour de Contratos",
        "clients-patients": "Tour de Clientes y Pacientes",
        invoices: "Tour de Facturas",
        employees: "Tour de Empleados",
        payments: "Tour de Pagos",
        scheduling: "Tour de Programación",
        "bank-reconciliation": "Tour de Conciliación Bancaria",
      };
      return tourNames[tourId] || tourId;
    };

    const getNextTourDescription = (nextTourId: string) => {
      const tour = getTourById(nextTourId);
      return (
        tour?.description ||
        "Continúa explorando más funcionalidades de la aplicación."
      );
    };

    return (
      <TourTransitionTooltip
        currentTourName={getTourName(tourState.currentTourId || "overview")}
        nextTourName={getTourName(currentStep.nextTour || "overview")}
        nextTourDescription={getNextTourDescription(
          currentStep.nextTour || "overview",
        )}
        onContinue={() => {
          // Start the next tour
          if (currentStep.nextTour) {
            completeTour();
            setTimeout(() => {
              startTour(currentStep.nextTour!);
            }, 100);
          }
        }}
        onGoBack={() => {
          // Go back to previous step
          prevStep();
        }}
        onSkip={() => {
          // Complete current tour and start the tour-menu-intro to remind user about tours
          completeTour();
          setTimeout(() => {
            startTour("tour-menu-intro");
          }, 100);
        }}
        isVisible={true}
        showBackButton={tourState.currentStep > 0}
      />
    );
  }

  const getTooltipPosition = () => {
    if (!overlayState.spotlightRect || overlayState.isTransitioning) {
      // Position off-screen instead of hiding with display: none
      return {
        top: "-9999px",
        left: "-9999px",
        opacity: 0,
      };
    }

    // Verify the target element matches the current step
    if (overlayState.targetElement) {
      const elementTourId =
        overlayState.targetElement.getAttribute("data-tour");
      if (elementTourId !== currentStep.target) {
        return {
          top: "-9999px",
          left: "-9999px",
          opacity: 0,
        };
      }
    }

    // Decide which rect anchors the tooltip based on tooltipTarget preference
    let rectToUse = overlayState.spotlightRect;
    if (currentStep.tooltipTarget === "secondary") {
      // For contracts create, add candidate, and tags, only use secondary rect if it's fully stable
      if (
        currentStep.target === "tour-contracts-create" ||
        currentStep.target === "tour-employees-add-candidate" ||
        currentStep.target === "tour-employees-tags"
      ) {
        if (
          overlayState.secondarySpotlightRect &&
          overlayState.secondarySpotlightRect.width > 50 &&
          overlayState.secondarySpotlightRect.height > 50
        ) {
          rectToUse = overlayState.secondarySpotlightRect;
        } else {
          // Don't show tooltip at all if secondary target is not ready
          return {
            top: "-9999px",
            left: "-9999px",
            opacity: 0,
          };
        }
      } else {
        // For other secondary targets, use original logic
        if (
          overlayState.secondarySpotlightRect &&
          overlayState.secondarySpotlightRect.width > 0 &&
          overlayState.secondarySpotlightRect.height > 0
        ) {
          rectToUse = overlayState.secondarySpotlightRect;
        } else {
          rectToUse = overlayState.spotlightRect;
        }
      }
    } else {
      // Default to primary when omitted or explicitly set to primary
      rectToUse = overlayState.spotlightRect;
    }

    const { top, left, width, height } = rectToUse;
    const placement = currentStep.placement || "bottom";

    const padding = 20;
    const tooltipWidth = 320;
    const tooltipHeight = 200;

    // Use cached tooltip dimensions or measure them
    let actualTooltipHeight = overlayState.cachedTooltipHeight || tooltipHeight;
    let actualTooltipWidth = overlayState.cachedTooltipWidth || tooltipWidth;

    if (!overlayState.cachedTooltipHeight || !overlayState.cachedTooltipWidth) {
      const tooltipElement = document.querySelector(
        "[data-tour-tooltip]",
      ) as HTMLElement;

      if (tooltipElement) {
        const rect = tooltipElement.getBoundingClientRect();
        if (rect.height > 0) {
          actualTooltipHeight = rect.height;
          setOverlayState((prev) => ({
            ...prev,
            cachedTooltipHeight: rect.height,
          }));
        }
        if (rect.width > 0) {
          actualTooltipWidth = rect.width;
          setOverlayState((prev) => ({
            ...prev,
            cachedTooltipWidth: rect.width,
          }));
        }
      }
    }

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Helper function to ensure popover stays within viewport
    const constrainToViewport = (
      x: number,
      y: number,
      customWidth?: number,
      allowNegativeY = false,
    ) => {
      const widthToUse = customWidth || tooltipWidth;
      const constrainedX = Math.max(
        padding,
        Math.min(x, viewportWidth - widthToUse - padding),
      );

      // Allow negative Y for top positioning (tooltip can go above viewport)
      const constrainedY = allowNegativeY
        ? Math.min(y, viewportHeight - tooltipHeight - padding)
        : Math.max(
            padding,
            Math.min(y, viewportHeight - tooltipHeight - padding),
          );

      return { x: constrainedX, y: constrainedY };
    };

    switch (placement) {
      case "top-left": {
        const { x, y } = constrainToViewport(
          left,
          top - actualTooltipHeight - padding,
          undefined,
          true,
        );
        return { top: `${y}px`, left: `${x}px` };
      }
      case "top-center": {
        // Use actual tooltip height for more accurate positioning
        const calculatedY = top - actualTooltipHeight - padding;
        const centerX = left + width / 2;
        const halfWidth = actualTooltipWidth / 2;

        // Calculate the actual position considering the transform
        const actualLeft = centerX - halfWidth;
        const actualRight = centerX + halfWidth;

        // Check if tooltip would go off screen and adjust accordingly
        let finalX = centerX;
        if (actualLeft < padding) {
          // Too far left, position at minimum padding
          finalX = padding + halfWidth;
        } else if (actualRight > viewportWidth - padding) {
          // Too far right, position at maximum right
          finalX = viewportWidth - padding - halfWidth;
        }

        // If there's not enough space above, make tooltip wider
        if (calculatedY < padding) {
          const widerTooltipWidth = Math.min(viewportWidth - 40, width + 200);
          const widerHalfWidth = widerTooltipWidth / 2;
          const widerActualLeft = centerX - widerHalfWidth;
          const widerActualRight = centerX + widerHalfWidth;

          let widerFinalX = centerX;
          if (widerActualLeft < padding) {
            widerFinalX = padding + widerHalfWidth;
          } else if (widerActualRight > viewportWidth - padding) {
            widerFinalX = viewportWidth - padding - widerHalfWidth;
          }

          return {
            top: `${calculatedY}px`,
            left: `${widerFinalX}px`,
            transform: "translateX(-50%)",
            width: `${widerTooltipWidth}px`,
          };
        }

        return {
          top: `${calculatedY}px`,
          left: `${finalX}px`,
          transform: "translateX(-50%)",
        };
      }
      case "top-right": {
        const { x, y } = constrainToViewport(
          left + width - tooltipWidth,
          top - actualTooltipHeight - padding,
          undefined,
          true,
        );
        return { top: `${y}px`, left: `${x}px` };
      }
      case "bottom":
        return {
          top: `${top + height + padding}px`,
          left: `${left + width / 2}px`,
          transform: "translateX(-50%)",
        };
      case "bottom-left": {
        const { x, y } = constrainToViewport(left, top + height + padding);
        return { top: `${y}px`, left: `${x}px` };
      }
      case "bottom-center":
        return {
          top: `${top + height + padding}px`,
          left: `${left + width / 2}px`,
          transform: "translateX(-50%)",
        };
      case "bottom-right": {
        const { x, y } = constrainToViewport(
          left + width - tooltipWidth,
          top + height + padding,
        );
        return { top: `${y}px`, left: `${x}px` };
      }
      case "left-top": {
        // Position tooltip to the left of the element, growing leftward
        const calculatedX = left - actualTooltipWidth - padding;

        // If there's not enough space to the left, position to the right instead
        if (calculatedX < padding) {
          const { x, y } = constrainToViewport(
            left + width + padding,
            top,
            undefined,
            false,
          );
          return { top: `${y}px`, left: `${x}px` };
        }

        const { x, y } = constrainToViewport(
          calculatedX,
          top,
          undefined,
          false,
        );
        return { top: `${y}px`, left: `${x}px` };
      }
      case "left-center": {
        // Use actual tooltip width for more accurate positioning
        const calculatedX = left - actualTooltipWidth - padding;

        // Check if tooltip would overlap with highlight
        // Use actual spotlight position for accurate collision detection
        const highlightLeft = overlayState.spotlightRect.left; // Use real spotlight position
        const tooltipRight = calculatedX + actualTooltipWidth;
        const safetyMargin = 20; // Add safety margin to detect close proximity
        const wouldOverlap = tooltipRight > highlightLeft - safetyMargin;

        if (wouldOverlap) {
          // Position tooltip further to the left to avoid overlap
          const adjustedX = highlightLeft - actualTooltipWidth - padding;
          const { x, y } = constrainToViewport(
            adjustedX,
            top + height / 2,
            undefined,
            false,
          );
          return {
            top: `${y}px`,
            left: `${x}px`,
            transform: "translateY(-50%)",
          };
        }

        // If there's not enough space to the left, position to the right instead
        if (calculatedX < padding) {
          const { x, y } = constrainToViewport(
            left + width + padding,
            top + height / 2,
            undefined,
            false,
          );
          return {
            top: `${y}px`,
            left: `${x}px`,
            transform: "translateY(-50%)",
          };
        }

        const { x, y } = constrainToViewport(
          calculatedX,
          top + height / 2,
          undefined,
          false,
        );
        return {
          top: `${y}px`,
          left: `${x}px`,
          transform: "translateY(-50%)",
        };
      }
      case "left-bottom": {
        const { x, y } = constrainToViewport(
          left - tooltipWidth - padding,
          top + height - tooltipHeight,
        );
        return { top: `${y}px`, left: `${x}px` };
      }
      case "right-top": {
        const { x, y } = constrainToViewport(left + width + padding, top);
        return { top: `${y}px`, left: `${x}px` };
      }
      case "right-center":
        return {
          top: `${top + height / 2}px`,
          left: `${left + width + padding}px`,
          transform: "translateY(-50%)",
        };
      case "right-bottom": {
        const x = left + width + padding;
        const y = top + height - actualTooltipHeight;
        return { top: `${y}px`, left: `${x}px` };
      }
      case "center":
      default:
        return {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="overlay"
        className="fixed inset-0 z-[10000] pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Dark overlay with cutouts for spotlights */}
        <div
          className="absolute inset-0 bg-black/50"
          style={{
            clipPath: (() => {
              if (!overlayState.spotlightRect) return "none";
              const padding = 0;
              const primaryLeft = Math.max(
                0,
                overlayState.spotlightRect.left - padding,
              );
              const primaryTop = Math.max(
                0,
                overlayState.spotlightRect.top - padding,
              );
              const primaryRight = Math.min(
                window.innerWidth,
                overlayState.spotlightRect.right + padding,
              );
              const primaryBottom = Math.min(
                window.innerHeight,
                overlayState.spotlightRect.bottom + padding,
              );

              if (overlayState.secondarySpotlightRect) {
                const secondaryLeft = Math.max(
                  0,
                  overlayState.secondarySpotlightRect.left - padding,
                );
                const secondaryTop = Math.max(
                  0,
                  overlayState.secondarySpotlightRect.top - padding,
                );
                const secondaryRight = Math.min(
                  window.innerWidth,
                  overlayState.secondarySpotlightRect.right + padding,
                );
                const secondaryBottom = Math.min(
                  window.innerHeight,
                  overlayState.secondarySpotlightRect.bottom + padding,
                );

                // Create a polygon that covers everything except the two spotlights
                const clipPathValue = `polygon(
                  0% 0%, 
                  0% 100%, 
                  ${primaryLeft}px 100%, 
                  ${primaryLeft}px ${primaryTop}px, 
                  ${primaryRight}px ${primaryTop}px, 
                  ${primaryRight}px ${primaryBottom}px, 
                  ${primaryLeft}px ${primaryBottom}px, 
                  ${primaryLeft}px 100%, 
                  ${secondaryLeft}px 100%, 
                  ${secondaryLeft}px ${secondaryTop}px, 
                  ${secondaryRight}px ${secondaryTop}px, 
                  ${secondaryRight}px ${secondaryBottom}px, 
                  ${secondaryLeft}px ${secondaryBottom}px, 
                  ${secondaryLeft}px 100%, 
                  100% 100%, 
                  100% 0%
                )`;

                return clipPathValue;
              } else {
                // Create a polygon that covers everything except the primary spotlight
                const clipPathValue = `polygon(
                0% 0%, 
                0% 100%, 
                  ${primaryLeft}px 100%, 
                  ${primaryLeft}px ${primaryTop}px, 
                  ${primaryRight}px ${primaryTop}px, 
                  ${primaryRight}px ${primaryBottom}px, 
                  ${primaryLeft}px ${primaryBottom}px, 
                  ${primaryLeft}px 100%, 
                100% 100%, 
                100% 0%
                )`;

                return clipPathValue;
              }
            })(),
          }}
        />

        {/* Primary spotlight border */}
        {overlayState.spotlightRect && (
          <div
            className="absolute bg-transparent border-2 border-orange-500 rounded-lg"
            style={{
              position: "fixed",
              top: `${overlayState.spotlightRect.top - 4}px`,
              left: `${overlayState.spotlightRect.left - 4}px`,
              width: `${overlayState.spotlightRect.width + 8}px`,
              height: `${overlayState.spotlightRect.height + 8}px`,
              pointerEvents: "none",
              zIndex: 10001,
            }}
          />
        )}

        {/* Secondary spotlight border */}
        {overlayState.secondarySpotlightRect && (
          <div
            className="absolute bg-transparent border-2 border-orange-500 rounded-lg"
            style={{
              position: "fixed",
              top: `${overlayState.secondarySpotlightRect.top - 4}px`,
              left: `${overlayState.secondarySpotlightRect.left - 4}px`,
              width: `${overlayState.secondarySpotlightRect.width + 8}px`,
              height: `${overlayState.secondarySpotlightRect.height + 8}px`,
              pointerEvents: "none",
              zIndex: 10001,
            }}
          />
        )}
      </motion.div>

      {/* Tooltip outside the overlay container */}
      <motion.div
        key="tooltip"
        data-tour-tooltip
        data-step-target={currentStep.target}
        className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 max-w-sm pointer-events-auto"
        style={(() => {
          const position = getTooltipPosition();
          return {
            position: "fixed" as const,
            zIndex: 10003,
            ...position,
          };
        })()}
        initial={{ opacity: 0 }}
        animate={{
          opacity: (() => {
            // For contracts create, add candidate, and tags steps, only show tooltip when secondary target is ready
            if (
              (currentStep.target === "tour-contracts-create" ||
                currentStep.target === "tour-employees-add-candidate" ||
                currentStep.target === "tour-employees-tags") &&
              currentStep.secondaryTarget
            ) {
              return overlayState.spotlightRect &&
                overlayState.secondarySpotlightRect &&
                !overlayState.isTransitioning
                ? 1
                : 0;
            }
            // For other steps, use original logic
            return overlayState.spotlightRect && !overlayState.isTransitioning
              ? 1
              : 0;
          })(),
        }}
        exit={{ opacity: 0 }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4 border-b border-gray-200 pb-4">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
              {tourState.currentStep + 1}
            </div>
            <span className="text-sm text-gray-500">
              de {tourState.steps.length}
            </span>
          </div>
          {tourState.currentTourId &&
            (() => {
              const tourInfo = getTourInfo(tourState.currentTourId);
              return tourInfo ? (
                <span className="text-sm font-bold">{tourInfo.name}</span>
              ) : null;
            })()}

          <Button
            variant="ghost"
            size="sm"
            onClick={completeTour}
            className="h-8 w-8 p-0"
          >
            <LuX className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <motion.div
          className="mb-6"
          initial={{ opacity: 0 }}
          animate={{
            opacity:
              overlayState.spotlightRect && !overlayState.isTransitioning
                ? 1
                : 0,
          }}
          transition={{ duration: 0.2, delay: 0.1 }}
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {currentStep.title}
          </h3>
          <div className="text-gray-600 text-sm leading-relaxed">
            <div
              className={`flex flex-wrap items-baseline ${
                isDetailsExpanded ? "overflow-y-auto" : ""
              }`}
              style={{
                maxHeight: isDetailsExpanded
                  ? `${Math.max(
                      80,
                      (overlayState.cachedTooltipHeight || 200) - 206,
                    )}px`
                  : undefined,
              }}
            >
              <span>
                {currentStep.content}

                {currentStep.details && !isDetailsExpanded && (
                  <Button
                    variant="link"
                    onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                    className="text-ac+1 hover:text-ac font-medium ml-1 transition-colors duration-200 p-0"
                  >
                    Saber más
                  </Button>
                )}
              </span>
              {/* Detalles expandibles con scroll */}
              {currentStep.details && isDetailsExpanded && (
                <p className="text-gray-600 text-sm leading-relaxed mt-3">
                  {currentStep.details}
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setIsDetailsExpanded(!isDetailsExpanded)}
                    className="text-ac+1 hover:text-ac font-medium ml-1 transition-colors duration-200 "
                  >
                    Ver menos
                  </Button>
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          className="flex items-center justify-between"
          initial={{ opacity: 0 }}
          animate={{
            opacity:
              overlayState.spotlightRect && !overlayState.isTransitioning
                ? 1
                : 0,
          }}
          transition={{ duration: 0.2, delay: 0.15 }}
        >
          <div className="flex items-center space-x-2">
            {tourState.currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={prevStep}
                className="h-8"
              >
                <LuChevronLeft className="h-4 w-4 ml-1" />
                Atrás
              </Button>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button onClick={nextStep}>
              {tourState.currentStep === tourState.steps.length - 1 ? (
                "Finalizar"
              ) : (
                <>
                  Siguiente
                  <LuChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>

      {/* Global pointer-events blocker - must be last to be on top */}
      <div
        className="fixed inset-0 z-[10002] pointer-events-auto"
        style={{
          pointerEvents: "auto",
          backgroundColor: "rgba(0, 0, 0, 0.001)",
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      />
    </AnimatePresence>
  );
};
